<template>
    <div>
        <h4 id="atmo-disputes-resolution">Согласование разногласий по дополнительному соглашению</h4>
        <h5 class="grey-color fw-600">Измените спецификацию</h5>
        <b-overlay :show="isDataSending" opacity="0.6" rounded spinner-small spinner-variant="primary">
            <fl44-contract-supplementary-agreement-termination-specifications-edit-table v-model="form.deliverables" :v="$v.form.deliverables" />
            <b-form-invalid-feedback :state="!$v.form.deliverables.$invalid" class="mt-3">{{ specErrorMessage }}</b-form-invalid-feedback>
            <text-row-price compact :label-cols-lg="4" label="Стоимость контракта" :value="contractPriceTotal" />
            <text-row-price compact :label-cols-lg="4" label="Стоимость контракта с учетом дополнительного соглашения" :value="specSum" />
            <form-row-edit-date :label-cols-lg="4" label="Дата заключения дополнительного соглашения" v-model="form.supplementary_agreement_date" :v="$v.form.supplementary_agreement_date" :min-date="purchase.contract_date" :max-date="$now()" compact />

            <form-row-single-line-document-upload v-model="form.supplementary_agreement" :label-cols-lg="4" :v="$v.form.supplementary_agreement" title="Новая редакция дополнительного соглашения" />
            <form-row-single-line-document-upload v-model="form.disagreement" :label-cols-lg="4" :v="$v.form.disagreement" title="Ответ на протокол разногласий" />
            <form-row-edit-textarea v-model="form.comment" :label-cols-lg="4" :v="$v.form.comment" label="Комментарий" />
            <b-btn :disabled="!$store.getters.getCanSign || $v.form.$invalid" class="mr-2 mt-2" variant="custom-green" @click="showSignForm">Подписать и отправить ответ на протокол разногласий</b-btn>
            <p class="font-weight-bold text-danger mt-3 mb-3 fs-14">Внимание! Для данной закупки будут заблокированы функции: изменение лимитов, добавление дополнительного соглашения, изменение статуса.</p>
        </b-overlay>
        <template v-if="!$store.getters.getCanSign">
            <span v-if="$store.getters.getLoginType === 'cert'" class="text-danger pl-3 pt-0 fs-14 w-50 my-auto">Для действия требуется загрузить/заменить МЧД на странице: <router-link :to="{ name: 'CabinetUserProfile' }" class="green-link">Данные учетной записи</router-link></span>
            <span v-else class="text-danger pl-3 pt-0 fs-14 w-50 my-auto">Подписание возможно только при авторизации при помощи ЭЦП</span>
        </template>

        <b-modal ref="sign-form-modal" v-model="showSignModal" centered no-close-on-backdrop no-close-on-esc scrollable size="lg" title="Подписать протокол разногласий" @close="cancelSignModal">
            <template #modal-header-close>
                <button-close-modal />
            </template>
            <div class="d-block text-left">
                <b-tabs content-class="mt-3 px-3">
                    <b-tab active>
                        <template #title>
                            <span class="system_link-style fs-16">Ответ на протокол разногласий</span>
                        </template>
                        <text-row-single-document compact :label-cols-lg="4" :label-cols-sm="4" label="Новая редакция дополнительного соглашения" :value="form.supplementary_agreement" />
                        <text-row-single-document compact :label-cols-lg="4" :label-cols-sm="4" label="Ответ на протокол разногласий" :value="form.disagreement" />
                        <text-row compact :label-cols-lg="4" :label-cols-sm="4" label="Комментарий" :value="form.comment" />
                        <h4 class="font-weight-bold">Спецификация</h4>
                        <b-form-group v-if="form.deliverables.length > 0">
                            <ul>
                                <li v-for="(item, index) in form.deliverables" :key="index" class="fs-14 grey-color my-auto col-form-label py-1">
                                    {{ index + 1 + '.' }} {{ item.name }} - {{ item.amount }} {{ item.okei.local_symbol }} * {{ $formatPrice(item.price) }} руб. = {{ $formatPrice(item.amount * item.price) }} руб.
                                </li>
                            </ul>
                        </b-form-group>
                    </b-tab>
                    <b-tab>
                        <template #title>
                            <span class="system_link-style fs-16">XML</span>
                        </template>
                        <b-form-textarea v-if="!isSignedXml" v-model="formXml" disabled rows="20" no-resize no-auto-shrink wrap="off" class="text-monospace fs-12"></b-form-textarea>
                        <b-form-textarea v-else v-model="signedFormXml" disabled rows="20" no-resize no-auto-shrink wrap="off" class="text-monospace fs-12"></b-form-textarea>
                    </b-tab>
                </b-tabs>
            </div>
            <div slot="modal-footer" class="w-100 px-3">
                <text-row compact label="Сертификат" :value="certificate?.subjectName" />
                <b-button :disabled="isDataSending" class="mr-2 text-uppercase" variant="custom-outline-secondary" @click="cancelSignModal">Отмена</b-button>
                <b-overlay v-if="!isSignedXml" :show="isSigningXml" class="d-inline-block" opacity="0.6" rounded spinner-small spinner-variant="primary">
                    <b-button :disabled="!certificate" class="text-uppercase" variant="custom-green" @click="signForm">Подписать</b-button>
                </b-overlay>
                <b-overlay v-else :show="isDataSending" class="d-inline-block" opacity="0.6" rounded spinner-small spinner-variant="primary">
                    <b-button :disabled="!certificate" class="text-uppercase" variant="custom-green" @click="storeSignedDisagreement">Отправить</b-button>
                </b-overlay>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { minLength, minValue, required } from 'vuelidate/lib/validators';
import { createMultipleSignatures, getCertificate } from 'crypto-pro';
import roundToTwoDecimals from '@lib/js/src/misc/roundToTwoDecimals';
import Fl44ContractSupplementaryAgreementTerminationSpecificationsEditTable from '@/components/products/fl44/partials/fl44-contract-supplementary-agreement-termination-specifications-edit-table.vue';

export default {
    name: 'private-fl44-contract-supplementary-agreement-termination-customer-reply-block',
    components: { Fl44ContractSupplementaryAgreementTerminationSpecificationsEditTable },
    props: {
        purchase: {
            type: Object,
            required: true
        },
        supplementaryAgreement: {
            type: Object,
            required: true
        },
        contractPriceTotal: {
            type: Number
        }
    },
    data() {
        return {
            showSignModal: false,
            isSigningXml: false,
            formXml: '',
            signedFormXml: null,
            signedAttachments: [],
            isSignedXml: false,
            isDataSending: false,
            certificate: null,
            showDisagreementModal: false,
            price: null,
            priceLimit: null,
            form: {
                supplementary_agreement_date: null,
                deliverables: [],
                supplementary_agreement: null,
                disagreement: null,
                comment: ''
            },
            signedSupplementaryAgreement: null,
            signedDisagreement: null,
            signedContract: null
        };
    },
    validations() {
        return {
            form: {
                supplementary_agreement_date: {
                    required
                },
                deliverables: {
                    required,
                    minLength: minLength(1),
                    priceLimit() {
                        return this.specSum <= this.priceLimit;
                    },
                    $each: {
                        ktru_item_id: { required },
                        amount: { required, minValue: minValue(0) },
                        minAmount(item) {
                            return item.amount >= item.min_amount;
                        },
                        maxAmount(item) {
                            return item.amount <= item.max_amount;
                        },
                        price: {
                            required
                        },
                        maxPrice(item) {
                            return item.price <= item.max_price;
                        }
                    }
                },
                supplementary_agreement: {
                    required
                },
                disagreement: {
                    required
                },
                comment: {
                    required,
                    minLength: minLength(3)
                }
            }
        };
    },
    async mounted() {
        await this.$api.personal.fl44
            .getSupplementaryAgreementTerminationTemplateData(this.purchase.id)
            .then((response) => {
                this.form.deliverables = this.supplementaryAgreement.deliverables.map((item) => {
                    return {
                        ...item,
                        max_amount: item.contract_amount,
                        min_amount: 0
                    };
                });
                this.form.supplementary_agreement_date = this.supplementaryAgreement.supplementary_agreement_date;
                this.price = response.price;
                this.priceLimit = response.max_price;
                this.$v.$reset();
            })
            .catch(() => {
                this.$emit('cancel');
            })
            .finally(() => {
                this.isLoading = false;
            });
    },
    methods: {
        async showSignForm() {
            if (!this.$store.getters.getCanSign) {
                return;
            }
            this.certificate = await getCertificate(this.$store.getters.getCurrentThumbprint);
            if (!this.certificate) {
                return;
            }
            this.$api.personal.fl44.getSupplementaryAgreementDisagreementXML(this.purchase.id, this.form).then((resp) => {
                this.formXml = resp.xml;
                this.showSignModal = true;
            });
        },

        async signForm() {
            this.isSigningXml = true;
            const dataToSign = [
                { method: 'xml', value: this.formXml },
                { method: 'detached', value: this.form.supplementary_agreement.hash, id: this.form.supplementary_agreement.id },
                { method: 'detached', value: this.form.disagreement.hash, id: this.form.disagreement.id }
            ];
            const signedResult = await createMultipleSignatures(this.$store.getters.getCurrentThumbprint, dataToSign, true);
            this.signedFormXml = signedResult.find((item) => item.method === 'xml').result;
            this.signedSupplementaryAgreement = signedResult.find((item) => item.method === 'detached' && item.value === this.form.supplementary_agreement.hash).result;
            this.signedDisagreement = signedResult.find((item) => item.method === 'detached' && item.value === this.form.disagreement.hash).result;
            this.isSignedXml = !!this.signedFormXml && this.signedDisagreement !== null && this.signedSupplementaryAgreement !== null;
            this.isSigningXml = false;
        },

        cancelSignModal() {
            this.showSignModal = false;
            this.isSignedXml = false;
            this.isSigningXml = false;
        },

        async storeSignedDisagreement() {
            this.isDataSending = true;
            const data = {
                certificate: await getCertificate(this.$store.getters.getCurrentThumbprint),
                xml: this.signedFormXml,
                comment: this.form.comment,
                supplementary_agreement_date: this.form.supplementary_agreement_date,
                supplementary_agreement: {
                    id: this.form.supplementary_agreement.id,
                    md5: this.signedSupplementaryAgreement
                },
                disagreement: {
                    id: this.form.disagreement.id,
                    md5: this.signedDisagreement
                },
                deliverables: this.form.deliverables
            };
            console.log(data);
            await this.$api.personal.fl44
                .storeSignedSupplementaryAgreementDisagreement(this.purchase.id, data, false)
                .then((result) => {
                    if (result) {
                        this.showDisagreementModal = false;
                        this.$emit('refresh', '#atmo-contract-annexes');
                    }
                })
                .finally(() => {
                    this.isDataSending = false;
                });
        }
    },
    computed: {
        specSum() {
            return roundToTwoDecimals(
                this.form.deliverables.reduce((sum, item) => {
                    return (sum += roundToTwoDecimals(item.amount * item.price));
                }, 0)
            );
        },
        specErrorMessage() {
            const deliverables = Object.values(this.$v.form.deliverables.$each.$iter);
            if (deliverables.some((e) => e.amount.$invalid)) return 'У одной или более позиций в таблице спецификации не заполнено поле "Количество фактическое"';
            if (!this.$v.form.deliverables.priceLimit) return `Сумма по ДС о расторжении не должна превышать сумму контракта / ДС (${this.$formatPrice(this.priceLimit)} руб.)`;
            return '';
        }
    }
};
</script>
